import {useEffect} from 'react';
import type {RefObject} from 'react';

/**
 * A file drop that misses the input navigates the tab to the file (SPA lost). The enclosing
 * panel swallows file dragover/drop so a missed drop inside it is a no-op. The native panel
 * listeners run BEFORE React's root-delegated handlers, but preventDefault only cancels the
 * browser navigation — the input's own handlers still receive the event and handle the file.
 * Scoped to file drags so dragging plain text into the textarea keeps its native behavior.
 */
export function usePreventDropNavigation(innerRef: RefObject<HTMLElement>) {
    useEffect(() => {
        const panel = innerRef.current?.closest('[role="dialog"]');
        if(!panel) {
            return;
        }
        const preventNavigation = (event: Event) => {
            if((event as DragEvent).dataTransfer?.types?.includes('Files')) {
                event.preventDefault();
            }
        };
        panel.addEventListener('dragover', preventNavigation);
        panel.addEventListener('drop', preventNavigation);
        return () => {
            panel.removeEventListener('dragover', preventNavigation);
            panel.removeEventListener('drop', preventNavigation);
        };
    }, [innerRef]);
}
